import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import axios from "axios";

import { baseURL } from "../utils/common";

const ORDER_DETAILS_URL = (id) => `${baseURL}/orders/admin/order/${id}/details/`;

const ORDER_COMMENT_UPDATE_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/comment/update/`;

const ORDER_ITEMS_UPDATE_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/items/update/`;

const ORDER_INVOICE_UPLOAD_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/invoice/upload/`;

const ORDER_ORG_DOCUMENTS_REQUEST_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/organization-documents/request/`;

const ORDER_ORG_DOCUMENTS_UPLOAD_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/organization-documents/upload/`;

const ORDER_DELIVERY_NOTE_UPLOAD_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/delivery-note/upload/`;

const ORDER_ESF_MARK_ISSUED_URL = (id) =>
    `${baseURL}/orders/admin/order/${id}/esf/mark-issued/`;

const ORDER_FINISH_URL = (id) => `${baseURL}/orders/admin/order/${id}/finish/`;

const ITEMS_URL = `${baseURL}/shop/admin/items/`;

const formatPrice = (value) => {
    return `${Number(value || 0).toLocaleString("ru-RU")} ₸`;
};

const getText = (value, lang = "ru") => {
    if (!value) return "—";
    if (typeof value === "string") return value || "—";

    return value[lang] || value.ru || "—";
};

const getErrorMessage = (error, fallback = "Ошибка соединения с сервером") => {
    return (
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        fallback
    );
};

const getStatusClass = (status) => {
    if (status === "Создан") return "order-status-created";
    if (status === "Товары проверены") return "order-status-items";
    if (status === "Счет выставлен") return "order-status-invoice";
    if (status === "Документы запрошены") return "order-status-docs-requested";
    if (status === "Документы получены") return "order-status-docs";
    if (status === "Накладная прикреплена") return "order-status-delivery";
    if (status === "ЭСФ выставлена") return "order-status-esf";
    if (status === "Закончен") return "order-status-finished";

    return "order-status-default";
};

const getStageClass = (stage) => {
    if (!stage) return "stage-pending";
    if (stage.status === "done") return "stage-done";
    return "stage-pending";
};

const fileIsReady = (fileUrl) => {
    return Boolean(fileUrl && fileUrl !== "—");
};

const makeDraftItems = (items) => {
    return (items || []).map((item, index) => {
        const retailPrice = Number(item.retail_price || item.price || 0);
        const currentPrice = Number(item.price || item.retail_price || 0);

        return {
            local_id: `${item.shop_item_id || item.id || item.product_id || "custom"}-${index}-${Date.now()}`,
            shop_item_id: item.shop_item_id || item.id || item.product_id || null,
            slug: item.slug || "",
            ru_name: item.ru_name || getText(item.name) || "—",
            name: item.name || null,
            image: item.image || "—",
            category: item.category || null,
            sub_category: item.sub_category || null,
            quantity: Number(item.quantity || item.count || 1),
            retail_price: retailPrice,
            price: currentPrice,
            opt_price: Number(item.opt_price || retailPrice || currentPrice || 0),
        };
    });
};

export const OrderDetailsPage = () => {
    const { id } = useParams();
    const history = useNavigate();

    const [order, setOrder] = useState(null);
    const [itemsDraft, setItemsDraft] = useState([]);
    const [comment, setComment] = useState("");

    const [invoiceFile, setInvoiceFile] = useState(null);
    const [paymentFile, setPaymentFile] = useState(null);
    const [powerOfAttorneyFile, setPowerOfAttorneyFile] = useState(null);
    const [deliveryNoteFile, setDeliveryNoteFile] = useState(null);
    const [fileInputVersion, setFileInputVersion] = useState(0);

    const [catalogItems, setCatalogItems] = useState([]);
    const [catalogFilters, setCatalogFilters] = useState(null);
    const [catalogSearch, setCatalogSearch] = useState("");
    const [catalogCategoryId, setCatalogCategoryId] = useState("");
    const [catalogSubCategoryId, setCatalogSubCategoryId] = useState("");
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogHasMore, setCatalogHasMore] = useState(false);
    const [catalogNextOffset, setCatalogNextOffset] = useState(null);

    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const resetFileInput = () => {
        setFileInputVersion((prev) => prev + 1);
    };

    const setFileFromEvent = (event, setter) => {
        setter(event.target.files?.[0] || null);
    };

    const postMultipart = async (url, formData) => {
        return axios({
            method: "post",
            url,
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data",
                Accept: "application/json",
            },
            transformRequest: [
                (data) => data,
            ],
        });
    };

    const getStage = (key) => {
        return (order?.stages || []).find((stage) => stage.key === key) || null;
    };

    const getItemsTotal = () => {
        return itemsDraft.reduce((sum, item) => {
            return sum + Number(item.price || 0) * Number(item.quantity || 0);
        }, 0);
    };

    const applyOrderResponse = (nextOrder) => {
        setOrder(nextOrder);
        setComment(nextOrder?.comment || "");
        setItemsDraft(makeDraftItems(nextOrder?.items || []));
    };

    const loadCatalogFiltersOnly = async () => {
        try {
            const response = await axios.get(ITEMS_URL, {
                params: {
                    limit: 1,
                    offset: 0,
                    ordering: "-id",
                },
            });

            const data = response.data;

            if (data.success) {
                setCatalogFilters(data.filters || null);
            }
        } catch (e) {
            console.log("Не удалось загрузить фильтры каталога", e);
        }
    };

    const loadCatalogItems = async (reset = true) => {
        if (catalogLoading) return;

        const hasSearch = catalogSearch.trim().length >= 2;
        const hasCategory = Boolean(catalogCategoryId);
        const hasSubCategory = Boolean(catalogSubCategoryId);

        if (!hasSearch && !hasCategory && !hasSubCategory) {
            setCatalogItems([]);
            setCatalogHasMore(false);
            setCatalogNextOffset(null);
            return;
        }

        setCatalogLoading(true);

        const offset = reset ? 0 : catalogNextOffset;

        const params = {
            limit: 8,
            offset: offset || 0,
            ordering: "-id",
        };

        if (catalogSearch.trim()) params.q = catalogSearch.trim();
        if (catalogCategoryId) params.category_id = catalogCategoryId;
        if (catalogSubCategoryId) params.sub_category_id = catalogSubCategoryId;

        try {
            const response = await axios.get(ITEMS_URL, { params });
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить товары");
                return;
            }

            if (reset) {
                setCatalogItems(data.results || []);
            } else {
                setCatalogItems((prev) => [...prev, ...(data.results || [])]);
            }

            setCatalogFilters(data.filters || null);
            setCatalogHasMore(Boolean(data.has_more));
            setCatalogNextOffset(data.next_offset);
        } catch (e) {
            setError(getErrorMessage(e, "Ошибка загрузки товаров"));
        } finally {
            setCatalogLoading(false);
        }
    };

    const loadOrder = async () => {
        setLoading(true);
        setError("");
        setMessage("");

        try {
            const response = await axios.get(ORDER_DETAILS_URL(id));
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить заказ");
                return;
            }

            applyOrderResponse(data.order || null);
            await loadCatalogFiltersOnly();
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const addCatalogItemToOrder = (item) => {
        setItemsDraft((prev) => {
            const exists = prev.find((draftItem) => {
                return draftItem.shop_item_id && draftItem.shop_item_id === item.id;
            });

            if (exists) {
                return prev.map((draftItem) => {
                    if (draftItem.shop_item_id !== item.id) return draftItem;

                    return {
                        ...draftItem,
                        quantity: Number(draftItem.quantity || 0) + 1,
                    };
                });
            }

            const retailPrice = Number(item.price || item.retail_price || 0);

            return [
                ...prev,
                {
                    local_id: `${item.id}-${Date.now()}-${Math.random()}`,
                    shop_item_id: item.id,
                    slug: item.slug || "",
                    ru_name: item.ru_name || getText(item.name) || "—",
                    name: item.name || null,
                    image: item.image || "—",
                    category: item.category || null,
                    sub_category: item.sub_category || null,
                    quantity: 1,
                    retail_price: retailPrice,
                    price: retailPrice,
                    opt_price: Number(item.opt_price || retailPrice || 0),
                },
            ];
        });

        setMessage("Товар добавлен в заказ. Не забудь сохранить товары.");
    };

    const updateItemQuantity = (localId, nextQuantity) => {
        const cleanQuantity = Number(nextQuantity || 0);

        setItemsDraft((prev) =>
            prev.map((item) => {
                if (item.local_id !== localId) return item;

                return {
                    ...item,
                    quantity: cleanQuantity < 1 ? 1 : cleanQuantity,
                };
            })
        );
    };

    const increaseItem = (localId) => {
        setItemsDraft((prev) =>
            prev.map((item) => {
                if (item.local_id !== localId) return item;

                return {
                    ...item,
                    quantity: Number(item.quantity || 0) + 1,
                };
            })
        );
    };

    const decreaseItem = (localId) => {
        setItemsDraft((prev) =>
            prev.map((item) => {
                if (item.local_id !== localId) return item;

                const nextQuantity = Number(item.quantity || 0) - 1;

                return {
                    ...item,
                    quantity: nextQuantity < 1 ? 1 : nextQuantity,
                };
            })
        );
    };

    const updateItemPrice = (localId, nextPrice) => {
        setItemsDraft((prev) =>
            prev.map((item) => {
                if (item.local_id !== localId) return item;

                return {
                    ...item,
                    price: Number(nextPrice || 0),
                };
            })
        );
    };

    const setItemRetailPrice = (localId) => {
        setItemsDraft((prev) =>
            prev.map((item) => {
                if (item.local_id !== localId) return item;

                return {
                    ...item,
                    price: Number(item.retail_price || item.price || 0),
                };
            })
        );
    };

    const setItemOptPrice = (localId) => {
        setItemsDraft((prev) =>
            prev.map((item) => {
                if (item.local_id !== localId) return item;

                return {
                    ...item,
                    price: Number(item.opt_price || item.price || 0),
                };
            })
        );
    };

    const removeItem = (localId) => {
        setItemsDraft((prev) => prev.filter((item) => item.local_id !== localId));
    };

    const saveComment = async () => {
        if (savingKey) return;

        setSavingKey("comment");
        setError("");
        setMessage("");

        try {
            const response = await axios.post(ORDER_COMMENT_UPDATE_URL(id), {
                comment,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить комментарий");
                return;
            }

            setMessage(data.message || "Комментарий сохранён");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const saveItems = async () => {
        if (savingKey) return;

        if (itemsDraft.length === 0) {
            setError("В заказе должен быть хотя бы один товар");
            return;
        }

        setSavingKey("items");
        setError("");
        setMessage("");

        try {
            const items = itemsDraft.map((item) => ({
                shop_item_id: item.shop_item_id,
                id: item.shop_item_id,
                slug: item.slug,
                ru_name: item.ru_name,
                name: item.name,
                category: item.category,
                sub_category: item.sub_category,
                quantity: Number(item.quantity || 1),
                count: Number(item.quantity || 1),
                retail_price: Number(item.retail_price || item.price || 0),
                price: Number(item.price || 0),
                opt_price: Number(item.opt_price || item.retail_price || item.price || 0),
                image: item.image,
            }));

            const response = await axios.post(ORDER_ITEMS_UPDATE_URL(id), {
                items: JSON.stringify(items),
                comment,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить товары");
                return;
            }

            setMessage(data.message || "Товары сохранены");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const uploadInvoice = async () => {
        if (savingKey) return;

        if (!invoiceFile) {
            setError("Прикрепите счет на оплату");
            return;
        }

        setSavingKey("invoice");
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("invoice", invoiceFile, invoiceFile.name);
            formData.append("invoice_file", invoiceFile, invoiceFile.name);
            formData.append("file", invoiceFile, invoiceFile.name);
            formData.append("comment", comment || "");

            const response = await postMultipart(ORDER_INVOICE_UPLOAD_URL(id), formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось прикрепить счет");
                return;
            }

            setInvoiceFile(null);
            resetFileInput();
            setMessage(data.message || "Счет прикреплен");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const requestOrgDocuments = async () => {
        if (savingKey) return;

        setSavingKey("org-request");
        setError("");
        setMessage("");

        try {
            const response = await axios.post(ORDER_ORG_DOCUMENTS_REQUEST_URL(id), {
                comment,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось отметить запрос документов");
                return;
            }

            setMessage(data.message || "Документы запрошены");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const uploadOrgDocuments = async () => {
        if (savingKey) return;

        if (!paymentFile && !powerOfAttorneyFile) {
            setError("Прикрепите платежное поручение / чек или доверенность");
            return;
        }

        setSavingKey("org-upload");
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("comment", comment || "");

            if (paymentFile) {
                formData.append("payment", paymentFile, paymentFile.name);
                formData.append("payment_file", paymentFile, paymentFile.name);
                formData.append("file", paymentFile, paymentFile.name);
            }

            if (powerOfAttorneyFile) {
                formData.append("power_of_attorney", powerOfAttorneyFile, powerOfAttorneyFile.name);
                formData.append("attorney", powerOfAttorneyFile, powerOfAttorneyFile.name);
                formData.append("attorney_file", powerOfAttorneyFile, powerOfAttorneyFile.name);
            }

            const response = await postMultipart(ORDER_ORG_DOCUMENTS_UPLOAD_URL(id), formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить документы организации");
                return;
            }

            setPaymentFile(null);
            setPowerOfAttorneyFile(null);
            resetFileInput();
            setMessage(data.message || "Документы организации сохранены");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const uploadDeliveryNote = async () => {
        if (savingKey) return;

        if (!deliveryNoteFile) {
            setError("Прикрепите накладную");
            return;
        }

        setSavingKey("delivery-note");
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("delivery_note", deliveryNoteFile, deliveryNoteFile.name);
            formData.append("delivery_note_file", deliveryNoteFile, deliveryNoteFile.name);
            formData.append("file", deliveryNoteFile, deliveryNoteFile.name);
            formData.append("comment", comment || "");

            const response = await postMultipart(ORDER_DELIVERY_NOTE_UPLOAD_URL(id), formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось прикрепить накладную");
                return;
            }

            setDeliveryNoteFile(null);
            resetFileInput();
            setMessage(data.message || "Накладная прикреплена");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const markEsfIssued = async () => {
        if (savingKey) return;

        setSavingKey("esf");
        setError("");
        setMessage("");

        try {
            const response = await axios.post(ORDER_ESF_MARK_ISSUED_URL(id), {
                comment,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось отметить ЭСФ");
                return;
            }

            setMessage(data.message || "ЭСФ выставлена");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    const finishOrder = async () => {
        if (savingKey) return;

        setSavingKey("finish");
        setError("");
        setMessage("");

        try {
            const response = await axios.post(ORDER_FINISH_URL(id), {
                comment,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось завершить заказ");
                return;
            }

            setMessage(data.message || "Заказ завершён");
            applyOrderResponse(data.order);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSavingKey("");
        }
    };

    useEffect(() => {
        loadOrder();
    }, [id]);

    useEffect(() => {
        if (!order) return;

        const timer = setTimeout(() => {
            loadCatalogItems(true);
        }, 350);

        return () => clearTimeout(timer);
    }, [catalogSearch, catalogCategoryId, catalogSubCategoryId]);

    if (loading) {
        return (
            <section className="order-details-page">
                <div className="order-details-loading">
                    Загрузка заказа...
                </div>
            </section>
        );
    }

    if (!order) {
        return (
            <section className="order-details-page">
                <button className="order-details-back" type="button" onClick={() => history("/orders")}>
                    ← Назад к заказам
                </button>

                <div className="order-details-error">
                    {error || "Заказ не найден"}
                </div>
            </section>
        );
    }

    return (
        <section className="order-details-page">
            <div className="order-details-head">
                <button className="order-details-back" type="button" onClick={() => history("/orders")}>
                    ← Назад к заказам
                </button>

                <div className="order-details-title-row">
                    <div>
                        <span>Заказ #{order.id}</span>
                        <h1>{order.client?.name || "—"}</h1>
                        <p>
                            {order.client?.client_type || "—"} · создан {order.created_at || "—"}
                        </p>
                    </div>

                    <div className={`order-details-status ${getStatusClass(order.order_status)}`}>
                        {order.order_status || "—"}
                    </div>
                </div>
            </div>

            {error && <div className="order-details-error">{error}</div>}
            {message && <div className="order-details-success">{message}</div>}

            <div className="order-details-summary">
                <div>
                    <span>Клиент</span>
                    <strong>{order.client?.name || "—"}</strong>
                    <p>{order.client?.bin_code || "—"}</p>
                </div>

                <div>
                    <span>Адрес</span>
                    <strong>{order.client?.address || "—"}</strong>
                    <p>{order.client?.client_type || "—"}</p>
                </div>

                <div>
                    <span>Менеджер</span>
                    <strong>{order.manager?.name || "—"}</strong>
                    <p>{order.manager?.email || "—"}</p>
                </div>

                <div>
                    <span>Сумма заказа</span>
                    <strong>{formatPrice(order.pay_sum)}</strong>
                    <p>Товары: {formatPrice(order.items_sum)}</p>
                </div>
            </div>

            <div className="order-details-stage-line">
                {(order.stages || []).map((stage) => (
                    <div className={`order-stage-dot ${getStageClass(stage)}`} key={stage.key}>
                        <strong>{stage.number}</strong>
                        <span>{stage.title}</span>
                    </div>
                ))}
            </div>

            <div className="order-comment-card">
                <div>
                    <h2>Комментарий</h2>
                    <p>Внутренний комментарий по заказу</p>
                </div>

                <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Например: клиент просил отгрузить после оплаты..."
                />

                <button type="button" onClick={saveComment} disabled={savingKey === "comment"}>
                    {savingKey === "comment" ? "Сохраняем..." : "Сохранить комментарий"}
                </button>
            </div>

            <div className="order-workflow">
                <article className={`order-stage-card ${getStageClass(getStage("items"))}`}>
                    <div className="order-stage-head">
                        <div>
                            <span>Этап 1</span>
                            <h2>Товары</h2>
                            <p>Добавляй товары из каталога, меняй количество и цены</p>
                        </div>

                        <strong>{formatPrice(getItemsTotal())}</strong>
                    </div>

                    <div className="order-add-products-box">
                        <div className="order-add-products-head">
                            <div>
                                <h3>Добавить товар из каталога</h3>
                                <p>Начни писать название товара или выбери категорию</p>
                            </div>
                        </div>

                        <div className="order-add-products-filters">
                            <label>
                                <span>Поиск</span>
                                <input
                                    value={catalogSearch}
                                    onChange={(e) => setCatalogSearch(e.target.value)}
                                    placeholder="Минимум 2 символа"
                                />
                            </label>

                            <label>
                                <span>Категория</span>
                                <select
                                    value={catalogCategoryId}
                                    onChange={(e) => {
                                        setCatalogCategoryId(e.target.value);
                                        setCatalogSubCategoryId("");
                                    }}
                                >
                                    <option value="">Все</option>
                                    {(catalogFilters?.category_id?.options || []).map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Подкатегория</span>
                                <select
                                    value={catalogSubCategoryId}
                                    onChange={(e) => setCatalogSubCategoryId(e.target.value)}
                                >
                                    <option value="">Все</option>
                                    {(catalogFilters?.sub_category_id?.options || []).map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="order-add-products-list">
                            {catalogItems.length === 0 && !catalogLoading && (
                                <div className="order-add-products-empty">
                                    {catalogSearch.trim().length < 2 && !catalogCategoryId && !catalogSubCategoryId
                                        ? "Начните писать название товара или выберите категорию"
                                        : "Товары не найдены"}
                                </div>
                            )}

                            {catalogItems.map((item) => (
                                <div className="order-add-product-item" key={item.id}>
                                    <div className="order-add-product-image">
                                        {item.image && item.image !== "—" ? (
                                            <img src={item.image} alt="" />
                                        ) : (
                                            <span>IMG</span>
                                        )}
                                    </div>

                                    <div className="order-add-product-info">
                                        <span>Товар #{item.id}</span>
                                        <strong>{item.ru_name || getText(item.name)}</strong>
                                        <p>
                                            {formatPrice(item.price)} · опт {formatPrice(item.opt_price)}
                                        </p>
                                    </div>

                                    <button type="button" onClick={() => addCatalogItemToOrder(item)}>
                                        Добавить
                                    </button>
                                </div>
                            ))}
                        </div>

                        {catalogLoading && (
                            <div className="order-add-products-empty">
                                Загрузка товаров...
                            </div>
                        )}

                        {catalogHasMore && (
                            <button
                                className="order-add-products-more"
                                type="button"
                                onClick={() => loadCatalogItems(false)}
                                disabled={catalogLoading}
                            >
                                {catalogLoading ? "Загрузка..." : "Еще товары"}
                            </button>
                        )}
                    </div>

                    {itemsDraft.length === 0 && (
                        <div className="order-stage-empty">
                            В заказе нет товаров
                        </div>
                    )}

                    {itemsDraft.length > 0 && (
                        <div className="order-items-editor">
                            {itemsDraft.map((item) => (
                                <div className="order-edit-item" key={item.local_id}>
                                    <div className="order-edit-item-image">
                                        {item.image && item.image !== "—" ? (
                                            <img src={item.image} alt="" />
                                        ) : (
                                            <span>IMG</span>
                                        )}
                                    </div>

                                    <div className="order-edit-item-info">
                                        <strong>{item.ru_name || getText(item.name)}</strong>
                                        <p>
                                            {item.category?.ru_name || "—"} / {item.sub_category?.ru_name || "—"}
                                        </p>
                                    </div>

                                    <div className="order-edit-qty">
                                        <button type="button" onClick={() => decreaseItem(item.local_id)}>
                                            −
                                        </button>

                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => updateItemQuantity(item.local_id, e.target.value)}
                                        />

                                        <button type="button" onClick={() => increaseItem(item.local_id)}>
                                            +
                                        </button>
                                    </div>

                                    <label className="order-edit-price">
                                        <span>Цена</span>
                                        <input
                                            type="number"
                                            value={item.price}
                                            onChange={(e) => updateItemPrice(item.local_id, e.target.value)}
                                        />
                                    </label>

                                    <div className="order-edit-price-actions">
                                        <button type="button" onClick={() => setItemRetailPrice(item.local_id)}>
                                            Розница
                                        </button>

                                        <button type="button" onClick={() => setItemOptPrice(item.local_id)}>
                                            Опт
                                        </button>
                                    </div>

                                    <div className="order-edit-total">
                                        <span>Итого</span>
                                        <strong>{formatPrice(item.price * item.quantity)}</strong>
                                    </div>

                                    <button
                                        className="order-edit-remove"
                                        type="button"
                                        onClick={() => removeItem(item.local_id)}
                                    >
                                        Убрать
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="order-stage-actions">
                        <button type="button" onClick={saveItems} disabled={savingKey === "items"}>
                            {savingKey === "items" ? "Сохраняем..." : "Сохранить товары"}
                        </button>
                    </div>
                </article>

                <article className={`order-stage-card ${getStageClass(getStage("invoice"))}`}>
                    <div className="order-stage-head">
                        <div>
                            <span>Этап 2</span>
                            <h2>Счет на оплату</h2>
                            <p>Прикрепи счет, который будет отправлен клиенту</p>
                        </div>
                    </div>

                    <div className="order-file-row">
                        <div>
                            <span>Текущий счет</span>
                            {fileIsReady(order.invoice) ? (
                                <a href={order.invoice} target="_blank" rel="noreferrer">
                                    Открыть файл
                                </a>
                            ) : (
                                <strong>Не прикреплен</strong>
                            )}
                        </div>

                        <label>
                            <span>Новый файл</span>
                            <input
                                key={`invoice-${fileInputVersion}`}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                onChange={(e) => setFileFromEvent(e, setInvoiceFile)}
                            />
                        </label>
                    </div>

                    <div className="order-stage-actions">
                        <button type="button" onClick={uploadInvoice} disabled={savingKey === "invoice"}>
                            {savingKey === "invoice" ? "Загружаем..." : "Прикрепить счет"}
                        </button>
                    </div>
                </article>

                {order.client?.is_organization && (
                    <article className={`order-stage-card ${getStageClass(getStage("organization_documents"))}`}>
                        <div className="order-stage-head">
                            <div>
                                <span>Этап 3.1</span>
                                <h2>Документы от организации</h2>
                                <p>Платежное поручение / чек и доверенность для отгрузки товара</p>
                            </div>
                        </div>

                        <div className="order-org-doc-note">
                            Для организации нужно запросить у клиента платежное поручение или чек, а также доверенность на получение товара.
                        </div>

                        <div className="order-stage-actions order-stage-actions-left">
                            <button
                                type="button"
                                onClick={requestOrgDocuments}
                                disabled={savingKey === "org-request"}
                            >
                                {savingKey === "org-request" ? "Отмечаем..." : "Документы запрошены"}
                            </button>
                        </div>

                        <div className="order-file-row order-file-row-two">
                            <div>
                                <span>Платежное поручение / чек</span>
                                {fileIsReady(order.payment) ? (
                                    <a href={order.payment} target="_blank" rel="noreferrer">
                                        Открыть файл
                                    </a>
                                ) : (
                                    <strong>Не прикреплено</strong>
                                )}
                            </div>

                            <label>
                                <span>Новый файл</span>
                                <input
                                    key={`payment-${fileInputVersion}`}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                    onChange={(e) => setFileFromEvent(e, setPaymentFile)}
                                />
                            </label>

                            <div>
                                <span>Доверенность</span>
                                {fileIsReady(order.power_of_attorney) ? (
                                    <a href={order.power_of_attorney} target="_blank" rel="noreferrer">
                                        Открыть файл
                                    </a>
                                ) : (
                                    <strong>Не прикреплена</strong>
                                )}
                            </div>

                            <label>
                                <span>Новый файл</span>
                                <input
                                    key={`power-of-attorney-${fileInputVersion}`}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                    onChange={(e) => setFileFromEvent(e, setPowerOfAttorneyFile)}
                                />
                            </label>
                        </div>

                        <div className="order-stage-actions">
                            <button
                                type="button"
                                onClick={uploadOrgDocuments}
                                disabled={savingKey === "org-upload"}
                            >
                                {savingKey === "org-upload" ? "Сохраняем..." : "Сохранить документы"}
                            </button>
                        </div>
                    </article>
                )}

                <article className={`order-stage-card ${getStageClass(getStage("delivery_note"))}`}>
                    <div className="order-stage-head">
                        <div>
                            <span>Этап 3</span>
                            <h2>Накладная</h2>
                            <p>Прикрепи накладную в систему</p>
                        </div>
                    </div>

                    <div className="order-file-row">
                        <div>
                            <span>Текущая накладная</span>
                            {fileIsReady(order.delivery_note) ? (
                                <a href={order.delivery_note} target="_blank" rel="noreferrer">
                                    Открыть файл
                                </a>
                            ) : (
                                <strong>Не прикреплена</strong>
                            )}
                        </div>

                        <label>
                            <span>Новый файл</span>
                            <input
                                key={`delivery-note-${fileInputVersion}`}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                onChange={(e) => setFileFromEvent(e, setDeliveryNoteFile)}
                            />
                        </label>
                    </div>

                    <div className="order-stage-actions">
                        <button
                            type="button"
                            onClick={uploadDeliveryNote}
                            disabled={savingKey === "delivery-note"}
                        >
                            {savingKey === "delivery-note" ? "Загружаем..." : "Прикрепить накладную"}
                        </button>
                    </div>
                </article>

                <article className={`order-stage-card ${getStageClass(getStage("esf"))}`}>
                    <div className="order-stage-head">
                        <div>
                            <span>Этап 4</span>
                            <h2>ЭСФ</h2>
                            <p>Отметь, что электронная счет-фактура выставлена</p>
                        </div>
                    </div>

                    <div className="order-esf-box">
                        <strong>ЭСФ не требует файла</strong>
                        <p>После выставления ЭСФ в системе учета просто отметь этот шаг выполненным.</p>
                    </div>

                    <div className="order-stage-actions">
                        <button type="button" onClick={markEsfIssued} disabled={savingKey === "esf"}>
                            {savingKey === "esf" ? "Отмечаем..." : "ЭСФ выставлена"}
                        </button>
                    </div>
                </article>

                <article className={`order-stage-card ${getStageClass(getStage("finish"))}`}>
                    <div className="order-stage-head">
                        <div>
                            <span>Финал</span>
                            <h2>Завершение заказа</h2>
                            <p>Закрыть заказ после прохождения всех стадий</p>
                        </div>
                    </div>

                    <div className="order-stage-actions">
                        <button type="button" onClick={finishOrder} disabled={savingKey === "finish"}>
                            {savingKey === "finish" ? "Завершаем..." : "Завершить заказ"}
                        </button>
                    </div>
                </article>
            </div>
        </section>
    );
};
